<template>
  <modal
    name="equipment-plan-modal"
    :height="isMobile ? '100%' : 'auto'"
    :width="isMobile ? '100%' : '60%'"
    :scrollable="true"
    @closed="resetForm()"
  >
    <!-- Header -->
    <div
      class="pt-3 pb-2 pr-4 pl-4 border-b border-gray-200 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 w-full flex items-center justify-between"
    >
      <h4 class="text-lg font-semibold">
        <i class="fas fa-tools mr-2"></i>
        Ekipman ile Planla
      </h4>
      <button @click="$modal.hide('equipment-plan-modal')">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <!-- Body -->
    <div class="p-6 bg-white dark:bg-gray-900 text-black dark:text-gray-200">
      <!-- Sipariş Bilgileri -->
      <div v-if="order" class="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <h5 class="font-semibold mb-2">Sipariş Bilgileri</h5>
        <div class="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span class="text-gray-600 dark:text-gray-400">Sipariş No:</span>
            <span class="ml-2 font-medium">{{ order.orderNo }}</span>
          </div>
          <div>
            <span class="text-gray-600 dark:text-gray-400">Müşteri:</span>
            <span class="ml-2 font-medium">{{ order.customer }}</span>
          </div>
          <div>
            <span class="text-gray-600 dark:text-gray-400">Ürün:</span>
            <span class="ml-2 font-medium">{{ order.productType }}</span>
          </div>
          <div>
            <span class="text-gray-600 dark:text-gray-400">Miktar:</span>
            <span class="ml-2 font-medium">{{ order.tonnage }} {{ order.unitType }}</span>
          </div>
        </div>
      </div>

      <!-- Ekipman Türü Seçimi -->
      <div class="mb-4">
        <label class="block text-sm font-medium mb-2">
          <i class="fas fa-layer-group mr-2"></i>
          Ekipman Türü Seçin
        </label>
        <p class="text-xs text-gray-600 dark:text-gray-400 mb-3">
          Seçtiğiniz ekipman türüne göre uygun araç ve ekipmanlar otomatik atanacaktır
        </p>

        <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div
            v-for="category in availableCategories"
            :key="category.key"
            @click.stop="toggleCategory(category.key)"
            :class="[
              'p-4 border-2 rounded-lg cursor-pointer transition-all',
              selectedCategories.includes(category.key)
                ? 'border-blue-600 bg-blue-50 dark:bg-blue-900 dark:bg-opacity-30'
                : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
            ]"
          >
            <div class="flex items-center justify-between mb-2">
              <span class="font-medium">{{ category.label }}</span>
              <i
                v-if="selectedCategories.includes(category.key)"
                class="fas fa-check-circle text-blue-600"
              ></i>
            </div>
            <p class="text-xs text-gray-600 dark:text-gray-400">
              {{ category.description }}
            </p>
            <div v-if="category.availableCount > 0" class="mt-2 text-xs text-green-600 dark:text-green-400">
              <i class="fas fa-boxes mr-1"></i>
              {{ category.availableCount }} adet mevcut
            </div>
          </div>
        </div>
      </div>

      <!-- Seçili Türler Özeti -->
      <div v-if="selectedCategories.length > 0" class="mb-4 p-4 bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20 rounded-lg">
        <p class="text-sm font-medium mb-2">Seçili Ekipman Türleri:</p>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="cat in selectedCategories"
            :key="cat"
            class="px-3 py-1 bg-blue-600 text-white rounded-full text-xs font-medium flex items-center gap-2"
          >
            {{ getCategoryLabel(cat) }}
            <i @click.stop="toggleCategory(cat)" class="fas fa-times cursor-pointer hover:text-red-300"></i>
          </span>
        </div>
        <p class="text-xs text-gray-600 dark:text-gray-400 mt-2">
          <i class="fas fa-info-circle mr-1"></i>
          Sistem, seçilen ekipman türlerine göre en uygun aracı otomatik olarak önerecektir.
        </p>
      </div>

      <!-- Actions -->
      <div class="mt-6 flex justify-end space-x-3">
        <button
          @click="$modal.hide('equipment-plan-modal')"
          class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          İptal
        </button>
        <button
          @click="planWithEquipment"
          :disabled="selectedCategories.length === 0 || processing"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i :class="processing ? 'fas fa-spinner fa-spin mr-2' : 'fas fa-check mr-2'"></i>
          {{ processing ? 'Planlanıyor...' : 'Ekipman ile Planla' }}
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import axios from 'axios';
import { inventory } from '@/networking/urlmanager';

export default {
  name: 'EquipmentPlanModal',
  props: {
    order: {
      type: Object,
      default: null
    },
    vehicles: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      processing: false, // Prevent double submissions
      equipmentList: [],
      selectedCategories: [],
      equipmentTypeRules: {
        'POMPA&HORTUM&DUSURUCU': {
          preferredVehicleTypes: ['TANKER', 'ÇEKICI'],
          requiredCapacity: 15,
          description: 'Pompa, hortum ve düşürücü için tank veya çekici araç önerilir'
        },
        GUVENLIK: {
          preferredVehicleTypes: ['TANKER', 'ÇEKICI', 'KAMYON', 'KAMYONET'],
          requiredCapacity: 1,
          description: 'Güvenlik ekipmanı her araçta taşınabilir'
        },
        YEDEK_PARCA: {
          preferredVehicleTypes: ['KAMYON', 'KAMYONET'],
          requiredCapacity: 2,
          description: 'Yedek parça için kamyon veya kamyonet uygundur'
        },
        DIGER: {
          preferredVehicleTypes: ['KAMYON', 'KAMYONET'],
          requiredCapacity: 3,
          description: 'Genel ekipman için kamyon tipi araç önerilir'
        }
      }
    };
  },
  computed: {
    isMobile() {
      return window.innerWidth < 768;
    },

    // Mevcut kategori listesi
    availableCategories() {
      const categories = [
        { key: 'POMPA&HORTUM&DUSURUCU', label: 'Pompa & Hortum & Düşürücü', description: this.equipmentTypeRules['POMPA&HORTUM&DUSURUCU'].description },
        { key: 'GUVENLIK', label: 'Güvenlik Ekipmanı', description: this.equipmentTypeRules.GUVENLIK.description },
        { key: 'YEDEK_PARCA', label: 'Yedek Parça', description: this.equipmentTypeRules.YEDEK_PARCA.description },
        { key: 'DIGER', label: 'Diğer', description: this.equipmentTypeRules.DIGER.description }
      ];

      // Her kategori için mevcut ekipman sayısını hesapla
      return categories.map(cat => ({
        ...cat,
        availableCount: this.equipmentList.filter(eq =>
          eq.category === cat.key &&
          (eq.current_location === 'BOSTA' || eq.current_location === 'GARAJDA')
        ).length
      }));
    }
  },
  watch: {
    order: {
      handler(newVal) {
        if (newVal) {
          this.loadEquipment();
        }
      },
      immediate: true
    }
  },
  methods: {
    async loadEquipment() {
      this.loading = true;
      try {
        const response = await axios.get(inventory.getAll, {
          params: {
            page: 0,
            limit: 1000,
            status: 'AKTIF'
          },
          headers: {
            Authorization: "Bareer " + this.$store.state.userData.token,
          }
        });

        if (response.data.code === 200) {
          this.equipmentList = response.data.data.detail || [];
        }
      } catch (error) {
        console.error('Error loading equipment:', error);
        this.$toast.error('Ekipman listesi yüklenirken hata oluştu');
      } finally {
        this.loading = false;
      }
    },

    toggleCategory(category) {
      const index = this.selectedCategories.indexOf(category);
      if (index > -1) {
        this.selectedCategories.splice(index, 1);
      } else {
        this.selectedCategories.push(category);
      }
    },

    getTransferLocationName(transferTo) {
      if (transferTo.startsWith('VEHICLE_')) {
        const vehicleId = transferTo.replace('VEHICLE_', '');
        const vehicle = this.vehicles.find(v => v.id == vehicleId);
        return vehicle ? vehicle.plate : 'Araç';
      }
      const locations = {
        'DEPOT': 'Depo',
        'GARAGE': 'Garaj',
        'BRANCH': 'Şube'
      };
      return locations[transferTo] || transferTo;
    },

    async planWithEquipment() {
      if (this.selectedCategories.length === 0) {
        this.$toast.warning('Lütfen en az bir ekipman türü seçin');
        return;
      }

      if (this.processing) {
        return;
      }

      this.processing = true;

      try {
        // Sadece ekipman türlerini gönder, recommendation sistemi en uygun aracı bulacak
        this.$emit('plan-with-equipment', {
          order: this.order,
          selectedCategories: this.selectedCategories
        });

        // Emit yapıldıktan HEMEN sonra processing'i false yap
        // Parent component'in inProgress kontrolünden geçebilmesi için
        this.processing = false;

        const categoryLabels = this.selectedCategories.map(c => this.getCategoryLabel(c)).join(', ');
        this.$toast.info(`${categoryLabels} ekipmanına göre en uygun araç aranıyor...`);

        this.$modal.hide('equipment-plan-modal');
      } catch (error) {
        console.error('Error planning with equipment:', error);
        this.$toast.error('Planlama sırasında hata oluştu');
        this.processing = false;
      }
    },

    getCategoryLabel(category) {
      const labels = {
        'POMPA&HORTUM&DUSURUCU': 'Pompa & Hortum & Düşürücü',
        'GUVENLIK': 'Güvenlik Ekipmanı',
        'YEDEK_PARCA': 'Yedek Parça',
        'DIGER': 'Diğer'
      };
      return labels[category] || category;
    },

    getLocationLabel(location) {
      const labels = {
        'BOSTA': 'Boşta',
        'ARACTA': 'Araçta',
        'GARAJDA': 'Garajda',
        'TRANSFERDE': 'Transferde'
      };
      return labels[location] || location;
    },

    getLocationClass(location) {
      const classes = {
        'BOSTA': 'px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800',
        'ARACTA': 'px-2 py-1 text-xs rounded-full bg-green-100 text-green-800',
        'GARAJDA': 'px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800',
        'TRANSFERDE': 'px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800'
      };
      return classes[location] || 'px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800';
    },

    resetForm() {
      this.selectedCategories = [];
      this.processing = false;
    }
  }
};
</script>
